import React, { useMemo } from 'react';
import { useWidgetBuilder } from '../context/WidgetBuilderContext';
import { useBuilderLabels } from '../context/BuilderLocaleContext';
import { useCompetitions } from '../hooks/useCompetitions';
import { useRoster } from '../hooks/useRoster';
import {
  FEATURED_MATCH_TYPES,
  MOCK_COMPETITIONS,
  normalizeWidgetType,
  PLAYER_PICKER_TYPES,
  TEAM_PICKER_TYPES,
} from './widgetTypes';
import SearchableCombobox from './SearchableCombobox';
import MatchPickerField from './MatchPickerField';
import { logoUrlForTeamName } from '../widgets/common/TeamBadge';

export default function CompetitionBar() {
  const t = useBuilderLabels();
  const { config, updateConfig } = useWidgetBuilder();
  const { data: apiCompetitions = [], isLoading } = useCompetitions();
  const competitions = apiCompetitions.length > 0 ? apiCompetitions : MOCK_COMPETITIONS;
  const widgetType = normalizeWidgetType(config.type);
  const showMatchPicker = FEATURED_MATCH_TYPES.has(widgetType);
  const showTeamPicker = TEAM_PICKER_TYPES.has(widgetType);
  const teamPickerAllLabel =
    widgetType === 'injuries'
      ? t('teamAllGrouped')
      : widgetType === 'player-list'
        ? null
        : t('teamFirstInTable');
  const teamPickerHint =
    widgetType === 'injuries'
      ? t('hintTeamInjuriesPicker')
      : widgetType === 'player-list'
        ? t('hintTeamPlayerListPicker')
        : t('hintTeamProfilePicker');
  const showPlayerPicker = PLAYER_PICKER_TYPES.has(widgetType);
  const { data: roster } = useRoster(
    config.competitionId,
    showTeamPicker || showPlayerPicker
  );

  const competitionOptions = useMemo(() => {
    const opts = [];
    for (const c of competitions.filter((x) => x.curated)) {
      opts.push({ value: c.id, label: c.label, group: t('groupFeatured') });
    }
    for (const c of competitions.filter((x) => !x.curated)) {
      opts.push({ value: c.id, label: c.label, group: t('groupAllLeagues') });
    }
    if (!competitions.some((x) => x.curated) && !competitions.some((x) => !x.curated)) {
      for (const c of competitions) {
        opts.push({ value: c.id, label: c.label, group: '' });
      }
    }
    return opts;
  }, [competitions, t]);

  function onCompetitionChange(competitionId) {
    updateConfig({
      competitionId,
      featuredMatchId: '',
      selectedTeam: '',
      selectedPlayer: '',
    });
  }

  return (
    <div className="relative z-30 shrink-0 border-b border-slate-200 bg-white px-4 py-3 flex flex-wrap items-end gap-4 overflow-visible">
      <SearchableCombobox
        label={t('competition')}
        value={config.competitionId}
        onChange={onCompetitionChange}
        options={competitionOptions}
        placeholder={t('searchCompetitions')}
        disabled={isLoading}
        emptyMessage={t('noCompetitions')}
        hint={
          isLoading
            ? t('loadingLeagues')
            : t('competitionsFilter', { count: competitions.length })
        }
        className="flex-1 min-w-[200px] max-w-md"
      />

      {showPlayerPicker && (
        <SearchableCombobox
          label={t('player')}
          value={config.selectedPlayer || ''}
          onChange={(selectedPlayer) => updateConfig({ selectedPlayer })}
          options={[
            { value: '', label: t('topScorer'), group: t('groupDefault') },
            ...(roster?.players || []).map((p) => ({
              value: p.id,
              label: p.name,
              sublabel: p.team,
              group: t('groupPlayers'),
            })),
          ]}
          placeholder={t('searchPlayers')}
          emptyMessage={t('noPlayer')}
          hint={t('hintPlayerPickerBar')}
          className="flex-1 min-w-[200px] max-w-md"
        />
      )}

      {showTeamPicker && (
        <SearchableCombobox
          label={t('team')}
          value={config.selectedTeam || ''}
          onChange={(selectedTeam) => updateConfig({ selectedTeam })}
          options={[
            ...(teamPickerAllLabel
              ? [{ value: '', label: teamPickerAllLabel, group: t('groupDefault') }]
              : []),
            ...(roster?.teams || []).map((team) => ({
              value: team.id,
              label: team.name,
              logoUrl: logoUrlForTeamName(team.name),
              group: t('groupClubs'),
            })),
          ]}
          placeholder={t('searchTeams')}
          emptyMessage={t('noTeam')}
          hint={teamPickerHint}
          className="flex-1 min-w-[200px] max-w-md"
        />
      )}

      {showMatchPicker && (
        <MatchPickerField className="flex-1 min-w-[240px] max-w-xl" />
      )}

      {!showMatchPicker && !showTeamPicker && !showPlayerPicker && (
        <span className="text-xs text-slate-400 pb-2">
          {isLoading ? t('loading') : t('competitionsCount', { count: competitions.length })}
        </span>
      )}
    </div>
  );
}
